import { Box, Grid } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { Delivery } from '@/services/db/delivery/entity';
import { useDeliveryDetailContentOptional } from '@/services/db/delivery/queries';
import { deliveryRowState } from '../utils/delivery-row-state';
import { DeliveryRelatedLink } from './delivery-related-link';
import { DeliveryRowActions, type RowActionCallbacks } from './delivery-row-actions';
import {
  isExpiredEventTriggeredCampaign,
  useDeliveryRelatedModel,
  useDeliveryRowPermission,
} from './delivery-row-expansion-hooks';
import { DeliveryTimestampStack } from './delivery-timestamp-stack';

export type DeliveryRowExpansionProps = {
  delivery: Delivery;
} & RowActionCallbacks;

// Skip past the parent DataTable's leading select + expander cells so the grid's
// first data column lines up under the "Date created" header. Pluma's built-in
// cell widths are selector = space-175 + cell-pis and expander = space-350 +
// 2*border-0125 + cell-pis; our expanded content sits in a full-width cell that
// already contributes one cell-pis of its own, so the net offset is
// space-175 + space-350 + 2*border + one cell-pis (cell-pis defaults to space-150).
// If Pluma changes those leading widths this will drift by a few pixels and need
// resyncing. Mirrors the Ember expansion's calc.
const GUTTER_OFFSET = `calc(${themeVars.space['175']} + ${themeVars.space['350']} + (${themeVars.border['width-0125']} * 2) + ${themeVars.space['150']})`;

/**
 * The per-row expanded panel: a four-column grid whose first cell is an empty
 * spacer (so the content lines up under the Date column), followed by the
 * related-message link, the timestamp/reason stack, and the action buttons.
 * Callbacks arrive already bound to this row's id.
 */
export function DeliveryRowExpansion({ delivery, onSend, onDelete, onRetry, onRedeliver }: DeliveryRowExpansionProps) {
  const { model, isLoading } = useDeliveryRelatedModel(delivery);
  const permission = useDeliveryRowPermission(delivery, model);
  const detailQuery = useDeliveryDetailContentOptional({ id: delivery.id });
  const detail = detailQuery.data;
  const rowState = deliveryRowState(delivery);
  const hideBody = detail?.hide_body === true;
  const isExpired = isExpiredEventTriggeredCampaign(delivery, model);
  // Gate the action column until the detail resolves too: Resend is disabled by `hideBody`
  // (retention off), which is only known once the detail content loads.
  const actionsLoading = isLoading || detailQuery.isLoading;

  return (
    <Grid
      columns="1fr 1fr 1fr 1fr"
      columnGap="0"
      rowGap="200"
      alignItems="flex-start"
      style={{ paddingLeft: GUTTER_OFFSET }}
      data-test-delivery-row-expansion={delivery.id}
    >
      {/* Content occupies columns 2–4; column 1 stays empty to line up under the
          table's Date-created column (explicit placement, so no spacer element). */}
      <Box px="250" minW="0" style={{ gridColumnStart: 2 }} data-test-delivery-row-expansion-info>
        <DeliveryRelatedLink model={model} isLoading={isLoading} />
      </Box>
      <Box px="250" minW="0" style={{ gridColumnStart: 3 }}>
        <DeliveryTimestampStack
          delivery={delivery}
          detail={detail}
          isRedeliverableAttempted={rowState.isRedeliverableAttempted}
        />
      </Box>
      <Box pl="250" textAlign="right" style={{ gridColumnStart: 4 }} data-test-delivery-row-expansion-action-buttons>
        <DeliveryRowActions
          delivery={delivery}
          rowState={rowState}
          permission={permission}
          hideBody={hideBody}
          isExpired={isExpired}
          isLoading={actionsLoading}
          callbacks={{ onSend, onDelete, onRetry, onRedeliver }}
        />
      </Box>
    </Grid>
  );
}
